import { useMutation } from "@apollo/client";
import {
  Button,
  createStyles,
  Divider,
  Grid,
  makeStyles,
  Typography,
} from "@material-ui/core";
import { MaterialUiPickersDate } from "@material-ui/pickers/typings/date";
import moment from "moment";
import { useSnackbar } from "notistack";
import React from "react";
import {
  BaseRow,
  deDE,
  Importer as CSVImporter,
  ImporterField,
} from "react-csv-importer";
import "react-csv-importer/dist/index.css";
import { IdentifierLookup } from "../../constants";
import { TestResult, TestType } from "../../graphql/baseTypes";
import { BATCH_CREATE_TESTS } from "../../graphql/mutations";
import {
  CreateTestInput,
  CreateTestsBatchMutation,
  CreateTestsBatchMutationVariables,
} from "../../graphql/types";
import {
  getTestTypeFromValue,
  getValuesForSelectedTestReason,
  TestReason,
} from "../../utils/testDocumentationRuleEngine";
import LocationFilter from "../Filters/LocationFilter";
interface Props {
  onFinish: () => any;
  username: string;
}

const useStyles = makeStyles((theme) =>
  createStyles({
    divider: {
      margin: "20px 0px",
    },
    title: {
      margin: "10px 0",
    },
  })
);

interface RowTyp extends BaseRow {
  identifier: string;
  lastname: string;
  date: string;
  userId: string;
  result: TestResult;
  issuedSelfTests: number;
  dataPrivacyAccepted: boolean;
  type: TestType;
  reason: TestReason;
  testId: string;
  firstname: string;
  email?: string;
  birthdate?: string;
  street?: string;
  zip?: string;
  city?: string;
  insuranceNumber: string;
}

const TestImporter: React.FC<Props> = ({ onFinish, username }) => {
  const [state, setLocationId] = React.useState<{
    locationId: string;
    companyId: string;
  }>();
  const { locationId, companyId } = state || {};
  const classes = useStyles();
  const { enqueueSnackbar } = useSnackbar();

  const [sync] = useMutation<
    CreateTestsBatchMutation,
    CreateTestsBatchMutationVariables
  >(BATCH_CREATE_TESTS, {
    onCompleted(error) {
      console.log(error);
      enqueueSnackbar("Testdaten wurden erfoglreich importiert", {
        variant: "success",
      });
    },
    onError(error) {
      console.log(error);
      enqueueSnackbar("Testdaten konnten nicht importiert werden", {
        variant: "error",
      });
    },
  });

  const setSelection = (
    id: string | MaterialUiPickersDate,
    companyId: string
  ) => {
    setLocationId({ locationId: id as string, companyId });
  };
  const resetSelection = () => {
    setLocationId(undefined);
  };

  return (
    <div>
      <Divider className={classes.divider} />
      <Typography variant="h4" color="secondary" className={classes.title}>
        Import
      </Typography>

      <Grid container direction="row" justify="space-between">
        <LocationFilter
          onChange={(locationId, companyId) =>
            setSelection(locationId, companyId!)
          }
          onClear={resetSelection}
          value={locationId}
        />
        <Button
          variant="outlined"
          style={{ marginLeft: 10 }}
          color="secondary"
          href={
            "https://mat-test-storage221317-dev.s3.eu-central-1.amazonaws.com/MAT_TEST_UPLOAD_VORLAGE.csv"
          }
          download="MAT_Testupload_Vorlage.csv"
        >
          CSV Vorlage herunterladen
        </Button>
      </Grid>
      <CSVImporter<RowTyp>
        assumeNoHeaders={false}
        locale={deDE}
        chunkSize={100}
        restartable={false}
        processChunk={async (rows) => {
          const items: Array<CreateTestInput> = rows.map(
            ({
              date,
              issuedSelfTests = 0,
              reason,
              result,
              type,
              testId,
              firstname,
              identifier,
              lastname,
              birthdate,
              insuranceNumber,
              city,
              email,
              street,
              zip,
            }) => {
              return {
                identifier: IdentifierLookup[identifier] || "insurance",
                companyId: companyId!,
                locationId: locationId!,
                patient: {
                  firstname,
                  lastname,
                  insuranceNumber: insuranceNumber,
                  ...(email && { email }),
                  address: {
                    street: street,
                    city,
                    zip,
                  },
                  ...(birthdate && {
                    birthdate: moment(birthdate, "DD.MM.YYYY").format(
                      "YYYY-MM-DD"
                    ),
                  }),
                },
                date: moment(date, "DD.MM.YYYY HH:mm").toISOString(),
                result,
                testId,
                userId: username,
                dataPrivacyAccepted: true,
                issuedSelfTests,
                qrGenerated: false,
                documentation: {
                  ...getValuesForSelectedTestReason(
                    getTestTypeFromValue(type),
                    reason
                  ),
                },
              };
            }
          );
          await sync({ variables: { input: items } });
          return;
        }}
        onClose={() => {
          onFinish();
        }}
      >
        <ImporterField name="testId" label="Testnummer" />
        <ImporterField name="identifier" label="Identifier Typ" />
        <ImporterField name="date" label="Datum" />
        <ImporterField name="result" label="Ergebnis" />
        <ImporterField name="type" label="Testdurchführung" />
        <ImporterField name="reason" label="Testgrund" />
        <ImporterField name="lastname" label="Nachname" />
        <ImporterField name="firstname" label="Vorname" />
        <ImporterField name="email" label="E-Mail" optional />
        <ImporterField name="birthdate" label="Geburtsdatum" />
        <ImporterField name="street" label="Straße" optional />
        <ImporterField name="zip" label="Postleitzahl" optional />
        <ImporterField name="city" label="Wohnort" optional />
        <ImporterField name="insuranceNumber" label="Identifier Nr." />
      </CSVImporter>
      {locationId ? null : (
        <Typography variant="subtitle1" color="primary">
          Bitte zuerst einen Standort auswählen
        </Typography>
      )}
      <Divider className={classes.divider} />
    </div>
  );
};

export default TestImporter;
